/**
 * Restores from tape: what is being brought back now (the robot fetching the tape, the drive reading it, how far),
 * what is waiting, what came back and where to. Whoever asked (or the archivist) may cancel an open one; the archivist
 * moves on by hand the ones no agent does (items archived before the agent).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatBytes, type RestoreStatus } from '@airpost/shared';
import { ApiError, get, post } from '../api.js';
import { t } from '../i18n.js';
import { TapeDrive, TapeDriveFront, TapeRobot } from '../tape-robot.js';
import { fmtDateInput, fmtDateTime } from '../util.js';

export interface RestoreDto {
  id: string;
  videoId: string;
  title: string;
  eventDate: string;
  fileName: string | null;
  durationS: number;
  barcode: string;
  target: string;
  manual: boolean;
  inMs: number | null;
  outMs: number | null;
  reason: string | null;
  status: RestoreStatus;
  progress: number;
  message: string | null;
  outputName: string | null;
  bytes: number | null;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  canCancel: boolean;
  canMove: boolean;
}

/** Milliseconds as the archive's timecode, HH:MM:SS:FF at 25 frames. */
export function tcOf(ms: number): string {
  const f = Math.floor((ms % 1000) / 40);
  const s = Math.floor(ms / 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}:${p(f)}`;
}

export const restoreStatusLabel = (s: RestoreStatus): string =>
  ({ requested: t('ltoStRequested'), loading: t('ltoStLoading'), reading: t('ltoStReading'), done: t('ltoStDone'), failed: t('ltoStFailed'), cancelled: t('ltoStCancelled') })[s];

export function RestoreStatusPill({ status }: { status: RestoreStatus }) {
  return <span className={`lto-pill st-${status}`}>{restoreStatusLabel(status)}</span>;
}

/** One restore: its item, its part, where it goes, how far it is; with the actions it allows. */
export function RestoreRow({ r, onChanged, onOpenItem, showTitle = true }: { r: RestoreDto; onChanged: (r: RestoreDto) => void; onOpenItem?: (videoId: string) => void; showTitle?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = async (url: string, body: unknown = {}) => {
    setBusy(true);
    setError(null);
    try {
      onChanged((await post<{ restore: RestoreDto }>(url, body)).restore);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const open = r.status === 'requested' || r.status === 'loading' || r.status === 'reading';
  return (
    <div className={`lto-restore st-${r.status}`}>
      <TapeDrive reading={r.status === 'reading'} barcode={r.barcode} />
      <div className="lto-restore-main">
        {showTitle && (
          <button type="button" className="lto-link lto-restore-title" onClick={() => onOpenItem?.(r.videoId)}>
            {r.title}
          </button>
        )}
        <div className="lto-restore-meta">
          <b className="lto-barcode-chip">{r.barcode}</b>
          <span>{r.inMs !== null && r.outMs !== null ? `${tcOf(r.inMs)} → ${tcOf(r.outMs)}` : t('ltoWholeFile')}</span>
          <span>→ {r.manual ? t('ltoManualTarget') : r.target}</span>
          <span className="muted">
            {r.requestedBy ?? '—'} · {fmtDateTime(r.createdAt)}
          </span>
        </div>
        {open && (
          <div className="lto-progress" aria-label={`${Math.round(r.progress * 100)}%`}>
            <span style={{ width: `${Math.max(r.status === 'requested' ? 0 : 3, Math.round(r.progress * 100))}%` }} />
          </div>
        )}
        {(r.message || r.outputName || r.reason) && (
          <div className="lto-restore-note">
            {r.status === 'done' && r.outputName ? (
              <>
                ✓ {r.outputName}
                {r.bytes ? ` · ${formatBytes(r.bytes)}` : ''}
              </>
            ) : (
              r.message
            )}
            {r.reason && <span className="muted"> · «{r.reason}»</span>}
          </div>
        )}
        {error && <div className="lto-err">{error}</div>}
      </div>
      <div className="lto-restore-side">
        <RestoreStatusPill status={r.status} />
        {open && <span className="lto-pct">{r.status === 'requested' ? '' : `${Math.round(r.progress * 100)}%`}</span>}
        <div className="lto-restore-actions">
          {r.canMove && r.status === 'requested' && (
            <button type="button" className="lto-btn sm" disabled={busy} onClick={() => void act(`/api/archive/restores/${r.id}/status`, { status: 'loading' })}>
              {t('ltoMoveLoading')}
            </button>
          )}
          {r.canMove && (
            <button type="button" className="lto-btn sm ok" disabled={busy} onClick={() => void act(`/api/archive/restores/${r.id}/status`, { status: 'done', outputName: window.prompt(t('ltoMoveDoneAsk')) || null })}>
              ✓ {t('ltoMoveDone')}
            </button>
          )}
          {r.canMove && (
            <button type="button" className="lto-btn sm" disabled={busy} onClick={() => void act(`/api/archive/restores/${r.id}/status`, { status: 'failed', message: window.prompt(t('ltoMoveFailedAsk')) || null })}>
              ✕ {t('ltoMoveFailed')}
            </button>
          )}
          {r.canCancel && (
            <button type="button" className="lto-btn sm ghost" disabled={busy} onClick={() => window.confirm(t('ltoCancelAsk')) && void act(`/api/archive/restores/${r.id}/cancel`)}>
              {t('cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

type Scope = 'open' | 'mine' | 'all';

/** The restores view: the one being brought back now in large, then the rest. */
export function RestoresView({ onOpenItem, onChanged }: { onOpenItem: (videoId: string) => void; onChanged: () => void }) {
  const [scope, setScope] = useState<Scope>('open');
  const [rows, setRows] = useState<RestoreDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seen = useRef<string | null>(null);
  const load = useCallback(() => {
    get<{ restores: RestoreDto[] }>(`/api/archive/restores?scope=${scope}&limit=200`)
      .then((d) => {
        setRows(d.restores);
        setError(null);
        // a restore moved on: the archive's figures (and the agent's state) are read again
        const sig = d.restores.map((r) => `${r.id}:${r.status}`).join(',');
        if (seen.current !== null && seen.current !== sig) onChanged();
        seen.current = sig;
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [scope, onChanged]);
  useEffect(() => {
    load();
    const h = window.setInterval(load, 3000);
    return () => window.clearInterval(h);
  }, [load]);
  const active = rows?.find((r) => r.status === 'loading' || r.status === 'reading') ?? null;
  const changed = (r: RestoreDto) => setRows((xs) => (xs ? xs.map((x) => (x.id === r.id ? r : x)) : xs));
  return (
    <section className="lto-main lto-restores">
      <div className="lto-toolbar">
        <div className="lto-seg" role="tablist">
          {(['open', 'mine', 'all'] as Scope[]).map((s) => (
            <button key={s} type="button" role="tab" aria-selected={scope === s} className={scope === s ? 'on' : ''} onClick={() => setScope(s)}>
              {s === 'open' ? t('ltoScopeOpen') : s === 'mine' ? t('ltoScopeMine') : t('ltoScopeAll')}
            </button>
          ))}
        </div>
        <span className="muted">{rows ? `${rows.length} ${t('ltoRestoresCount')}` : ''}</span>
      </div>
      {error && <div className="lto-err">{error}</div>}
      {active && (
        <div className="lto-now">
          <div className="lto-now-art">{active.status === 'loading' ? <TapeRobot barcode={active.barcode} caption={t('ltoRobotFetch')} /> : <TapeDriveFront reading barcode={active.barcode} progress={active.progress} />}</div>
          <div className="lto-now-text">
            <div className="lto-now-kicker">{active.status === 'loading' ? t('ltoNowLoading') : t('ltoNowReading')}</div>
            <button type="button" className="lto-link lto-now-title" onClick={() => onOpenItem(active.videoId)}>
              {active.title}
            </button>
            <div className="muted">
              {fmtDateInput(active.eventDate)} · {active.inMs !== null && active.outMs !== null ? `${tcOf(active.inMs)} → ${tcOf(active.outMs)}` : t('ltoWholeFile')} · → {active.target}
            </div>
            <div className="lto-progress big">
              <span style={{ width: `${Math.max(3, Math.round(active.progress * 100))}%` }} />
            </div>
            <div className="lto-now-msg">{active.message}</div>
          </div>
        </div>
      )}
      <div className="lto-restore-list">
        {rows && rows.length === 0 && <div className="lto-empty">{scope === 'open' ? t('ltoNoOpenRestores') : t('ltoNoRestores')}</div>}
        {rows?.map((r) => <RestoreRow key={r.id} r={r} onChanged={changed} onOpenItem={onOpenItem} />)}
      </div>
    </section>
  );
}
